import React from 'react';
import { MainLayout } from 'layouts/MainLayout';
import { Box, Button, Paper } from '@mui/material';
import { customerExportsApi } from 'components/customers/export/customerExportsApi';
import { ICustomerExportOperation, CUSTOMER_OPERATIONS_LIMIT } from 'components/customers/export/models';
import { updateArrayItem } from 'lib/helpers';
import { CustomerExportedDataTable } from 'components/customers/export/tables/CustomerExportedDataTable';
import { useInterval } from 'lib/hooks/useInterval';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { MESSAGE_CUSTOMER_EXPORT_ERROR, MESSAGE_CUSTOMER_EXPORT_SUCCESS } from 'config/messages';
import logger from 'lib/logger';
import { useSnackbar } from 'notistack';
import { CustomerExportDrawer } from 'components/customers/export/drawer/CustomerExportDrawer';
import { ErrorLayout } from 'layouts/ErrorLayout';
import { CUSTOMERS } from 'config/routes';
import { Row } from 'ui/Flex';
import { ExportCustomersDialog } from 'components/customers/export/ExportCustomersDialog';

function filterPendingOperations(item: ICustomerExportOperation) {
    return item.status === OperationStatus.PENDING || item.status === OperationStatus.RUNNING;
}

function mergeOperations(oOne: ICustomerExportOperation[], oTwo: ICustomerExportOperation[]) {
    return oOne.map(item => {
        const newOperation = oTwo.find(operation => operation._id === item._id);
        if (newOperation) {
            return newOperation;
        }
        return item;
    });
}

export const ExportCustomersPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [exportOpen, setExportOpen] = React.useState(false);
    const [pendingOperations, setPendingOperations] = React.useState<ICustomerExportOperation[]>([]);
    const [isOperationsLoading, setOperationsLoading] = React.useState(false);
    const [exportOperations, setExportOperations] = React.useState<ICustomerExportOperation[]>([]);
    const [operationsError, setOperationsError] = React.useState<string>();
    const [selectedOperationId, setSelectedOperationId] = React.useState<string>(undefined);
    const [page, setPage] = React.useState(0);
    const [pages, setPages] = React.useState<(string | undefined)[]>([undefined]);
    const [endOfData, setEndOfData] = React.useState(false);

    useInterval(() => {
        if (pendingOperations.length) {
            const promises = pendingOperations.map(item => customerExportsApi.getOperation(item._id));
            Promise.all(promises)
                .then(results => {
                    const resultOperations = results.map(item => item.body);
                    const pending = resultOperations.filter(filterPendingOperations);
                    setPendingOperations(pending);
                    if (resultOperations.length - pending.length) {
                        enqueueSnackbar(MESSAGE_CUSTOMER_EXPORT_SUCCESS, { variant: 'success' });
                    }
                    setExportOperations(current => mergeOperations(current, resultOperations));
                })
                .catch(e => {
                    logger.error(e.message);
                    enqueueSnackbar(MESSAGE_CUSTOMER_EXPORT_ERROR, { variant: 'error' });
                });
        }
    }, 1500);

    const selectedOperation = React.useMemo(
        () => exportOperations.find(item => item._id === selectedOperationId),
        [selectedOperationId, exportOperations]
    );

    React.useEffect(() => {
        const loadPage = async () => {
            if (endOfData) {
                return;
            }
            setOperationsLoading(true);
            try {
                const startKey = pages[page];
                const res = await customerExportsApi.getOperations(
                    startKey ? { nextKey: startKey } : undefined
                );
                if (!res.body.page.nextKey) {
                    setEndOfData(true);
                }
                setExportOperations(current => [...current, ...res.body.items]);
                setPendingOperations(current => [
                    ...current,
                    ...res.body.items.filter(filterPendingOperations)
                ]);
                setPages(current => updateArrayItem(current, page + 1, res.body.page.nextKey));
            } catch (e) {
                setExportOperations([]);
                setOperationsError(e.message);
            } finally {
                setOperationsLoading(false);
            }
        };

        // load page if initial load (page 0 with no data) OR navigating to a page we haven't fetched yet
        const isInitialLoad = page === 0 && exportOperations.length === 0;
        const needsNextPage =
            !pages[page + 1] && exportOperations.length < pages.length * CUSTOMER_OPERATIONS_LIMIT;
        if (isInitialLoad || needsNextPage) {
            loadPage();
        }
    }, [page, pages, exportOperations.length, endOfData]);

    const handlePageChange = React.useCallback(
        (newPage: number) => {
            if (!isOperationsLoading) {
                setPage(newPage);
            }
        },
        [isOperationsLoading]
    );

    const count = React.useMemo(() => {
        if (endOfData) {
            return exportOperations.length;
        }
        return -1;
    }, [endOfData, exportOperations.length]);

    const currentPageOperations = React.useMemo(
        () =>
            exportOperations.slice(page * CUSTOMER_OPERATIONS_LIMIT, (page + 1) * CUSTOMER_OPERATIONS_LIMIT),
        [exportOperations, page]
    );

    const handleOperationClick = React.useCallback((operation: ICustomerExportOperation) => {
        setSelectedOperationId(operation._id);
    }, []);

    const handleDrawerClose = React.useCallback(() => {
        setSelectedOperationId(undefined);
    }, []);

    const handleCustomerExportCreation = React.useCallback((exportOperation: ICustomerExportOperation) => {
        setPendingOperations(currentOperations => [...currentOperations, exportOperation]);
        setExportOperations(currentOperations => [exportOperation, ...currentOperations]);
        setPage(0);
    }, []);

    const handleExportClick = React.useCallback(() => {
        setExportOpen(true);
    }, []);

    const handleExportClose = React.useCallback(() => {
        setExportOpen(false);
    }, []);

    return (
        <MainLayout pageName="Export Customers" pageDescription="Export your customer data." noScroll>
            {!!operationsError && (
                <ErrorLayout title="Unexpected Error" fallbackUrl={CUSTOMERS} fallbackTitle="Customers" />
            )}
            {!operationsError && (
                <>
                    <Box height="100%" display="flex" flexDirection="column">
                        <Box paddingBottom={1}>
                            <Row align="flex-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={handleExportClick}
                                >
                                    Create a new Export
                                </Button>
                            </Row>
                        </Box>
                        <Paper sx={{ flex: 1, minHeight: 0 }}>
                            <CustomerExportedDataTable
                                operations={currentPageOperations}
                                onClick={handleOperationClick}
                                page={page}
                                count={count}
                                loading={isOperationsLoading}
                                onPageChange={handlePageChange}
                            />
                        </Paper>
                    </Box>
                    <CustomerExportDrawer onClose={handleDrawerClose} operation={selectedOperation} />
                    <ExportCustomersDialog
                        open={exportOpen}
                        onCancel={handleExportClose}
                        onCustomerExportCreation={handleCustomerExportCreation}
                    />
                </>
            )}
        </MainLayout>
    );
};
